import { useMultiWebSocketApi, useWebSocketApi } from '@e3dc-react/shell/Hooks'
import { SocketStateModel } from '@e3dc-react/shell/Hooks/use-websocket-api'
import { isUndefined } from 'lodash'
import { WallboxChargingModel } from '../models'
import { WallboxChargingDto } from '../api'
import { fromChargingDtoToModel } from '../transformers'
import { assertIsChargingModel } from 'src/Libs/assertions'
import { useEffect, useMemo, useState } from 'react'
import { useFarmContext } from 'src/Elements/Farm'
import { removeFinishedSession, upsertActiveSession } from '../utils'
import { isFlowTheme } from 'src/Libs/utils'

const SERVICE_ID = '[WALLBOX CHARGING LIVEDATA]'
const getResourcePath = (storageId: string | undefined): string => `wallboxes/${storageId}/ws`

/**
 * This hook provides wallboxes active charging through a websocket connection
 */
const useLiveWallboxCharging = (storageId: string | undefined, hasWallbox: boolean): SocketStateModel<WallboxChargingModel[]> => {
  const { isFarmMasterSelected, farmClients } = useFarmContext()
  const [data, setData] = useState<WallboxChargingModel[]>([])

  const resourcePath = getResourcePath(storageId)
  const endpoint = process.env.REACT_APP_EMOBILITY_ENDPOINT ?? ''

  const { socketState } = useWebSocketApi<WallboxChargingModel, WallboxChargingDto>({
    apiResourcePath: resourcePath,
    loggerKey: SERVICE_ID,
    transformer: fromChargingDtoToModel,
    doNotInitialize: isUndefined(storageId) || !hasWallbox || isFarmMasterSelected,
    dependencies: [storageId],
    endpoint,
  })
  const wallboxFromClientOnlyWebSocketApiPaths = useMemo(() => farmClients?.map(client => getResourcePath(client.storageId)) || [], [farmClients])
  const wallboxFromFarmMasterOnlyWebSocketApiPaths = useMemo(
    () => (isFarmMasterSelected ? [getResourcePath(storageId)] : []),
    [isFarmMasterSelected, storageId]
  )
  // Flow sub-devices are linked also to the farm master , not just the clients.
  const wallboxWebSocketApiPaths = useMemo(
    () =>
      isFlowTheme
        ? [...wallboxFromClientOnlyWebSocketApiPaths, ...wallboxFromFarmMasterOnlyWebSocketApiPaths]
        : wallboxFromClientOnlyWebSocketApiPaths,
    [wallboxFromClientOnlyWebSocketApiPaths, wallboxFromFarmMasterOnlyWebSocketApiPaths]
  )

  const { multiSocketState } = useMultiWebSocketApi<WallboxChargingModel, WallboxChargingDto>({
    apiResourcePaths: wallboxWebSocketApiPaths,
    loggerKey: SERVICE_ID,
    transformer: fromChargingDtoToModel,
    dependencies: [storageId],
    doNotInitialize: isUndefined(storageId) || !hasWallbox || !isFarmMasterSelected,
    endpoint,
  })

  useEffect(() => {
    const handleChargingSessions = (chargingModel: WallboxChargingModel | undefined): void => {
      if (!chargingModel) return

      assertIsChargingModel(chargingModel)

      /* Remove finished session. */
      if (chargingModel.stopAt) {
        setData(previous => removeFinishedSession(previous, chargingModel))
        return
      }

      /* Upsert active session so energyAll is updated in place, not duplicated. */
      setData(previous => upsertActiveSession(previous, chargingModel))
    }

    if (isFarmMasterSelected) {
      for (const chargingModel of Object.values(multiSocketState.data || {})) {
        handleChargingSessions(chargingModel)
      }
    } else {
      handleChargingSessions(socketState.data)
    }
  }, [socketState.data, multiSocketState.data, isFarmMasterSelected])

  return {
    ...socketState,
    data,
    connected: isFarmMasterSelected ? multiSocketState.connected : socketState.connected,
    error: isFarmMasterSelected ? multiSocketState.error : socketState.error,
  }
}

export default useLiveWallboxCharging
